<template>
  <div class="trade-chat-logs container-fluid py-4">
    <div
      class="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-3"
    >
      <div>
        <h2>Trade Chat Logs</h2>
        <p class="text-muted mb-0">
          Every captured Refinitiv Messenger message, searchable, with the trade
          signals extracted from it.
        </p>
      </div>
      <p
        class="agent-hint small text-muted mb-0"
        title="MCP tools for agents on a sales API key"
      >
        <FontAwesomeIcon icon="robot" /> Agents:
        <code>search_trade_chat_messages</code>,
        <code>get_trade_chat_message</code>
      </p>
    </div>
    <TradeChatFiltersComponent v-model="filters" />
    <div
      class="workspace mt-3"
      :class="{ 'has-detail': selectedMessageId !== null }"
    >
      <TradeChatConversationListComponent
        v-model:search="conversationSearch"
        :conversations="conversations.page.items"
        :total="conversations.page.total"
        :status="conversations.status"
        :selectedId="filters.conversationId"
        @select="selectConversation"
        @retry="retryConversations"
      />
      <TradeChatMessageListComponent
        :messages="messages.page.items"
        :total="messages.page.total"
        :status="messages.status"
        :hasMore="messages.page.has_more"
        :loadingMore="messages.loadingMore"
        :moreFailed="messages.moreFailed"
        :terms
        :selectedId="selectedMessageId"
        :showConversation="filters.conversationId === null"
        @select="selectMessage"
        @more="loadMore"
        @retry="retryMessages"
      />
      <template v-if="selectedMessageId !== null">
        <div
          v-if="detail.status === 'loading'"
          class="detail-placeholder"
          role="status"
        >
          Loading message…
        </div>
        <div
          v-else-if="detail.status === 'error'"
          class="detail-placeholder"
          role="alert"
        >
          <p>Could not load this message.</p>
          <button class="btn btn-outline-primary btn-sm" @click="retryDetail">
            Try again
          </button>
          <button class="btn btn-link btn-sm" @click="selectMessage(null)">
            Close
          </button>
        </div>
        <TradeChatMessageDetailComponent
          v-else-if="detail.data"
          :message="detail.data"
          :terms
          @close="selectMessage(null)"
          @select="selectMessage"
          @openConversation="selectConversation"
        />
      </template>
    </div>
  </div>
</template>
<script setup lang="ts">
import { onKeyStroke, refDebounced } from "@vueuse/core";
import { computed, ref } from "vue";
import TradeChatConversationListComponent from "./TradeChatConversationListComponent.vue";
import TradeChatFiltersComponent from "./TradeChatFiltersComponent.vue";
import TradeChatMessageDetailComponent from "./TradeChatMessageDetailComponent.vue";
import TradeChatMessageListComponent from "./TradeChatMessageListComponent.vue";
import { includedSearchTerms, toMessageQuery } from "./tradeChatLogsPresenters";
import { useTradeChatLogQuery } from "./useTradeChatLogQuery";
import {
  useChatConversations,
  useChatMessageDetail,
  useChatMessageList,
} from "./useTradeChatLogResources";

const { filters, selectedMessageId, selectMessage } = useTradeChatLogQuery();
const conversationSearch = ref("");

const terms = computed(() => includedSearchTerms(filters.value.q));
const messageQuery = computed(() => toMessageQuery(filters.value));
const debouncedConversationSearch = refDebounced(conversationSearch, 250);

const {
  state: messages,
  loadMore,
  retry: retryMessages,
} = useChatMessageList(messageQuery);
const { state: conversations, retry: retryConversations } =
  useChatConversations(debouncedConversationSearch);
const { state: detail, retry: retryDetail } =
  useChatMessageDetail(selectedMessageId);

onKeyStroke("Escape", () => {
  if (selectedMessageId.value !== null) selectMessage(null);
});

function selectConversation(conversationId: string | null) {
  filters.value = { ...filters.value, conversationId };
}
</script>
<style scoped lang="scss">
.trade-chat-logs {
  font-family: "Nunito Sans", var(--phoenix-font-sans-serif);
  --phoenix-border-color: color-mix(
    in srgb,
    var(--phoenix-body-color) 15%,
    transparent
  );
}
h2 {
  font-family: inherit;
  font-size: 1.65rem;
  font-weight: 700;
}
:deep(h5),
:deep(h6) {
  font-family: inherit;
}
.workspace {
  display: grid;
  grid-template-columns: minmax(220px, 300px) minmax(0, 1fr);
  gap: 1rem;
  align-items: start;

  &.has-detail {
    grid-template-columns: minmax(220px, 280px) minmax(0, 1fr) minmax(
        340px,
        460px
      );
  }
}
.detail-placeholder {
  text-align: center;
  padding: 3rem 1.5rem;
  border: 1px solid var(--phoenix-border-color);
  border-radius: 6px;
  background: var(--phoenix-body-bg);
  color: var(--phoenix-secondary-color);
}
@media (max-width: 1300px) {
  .workspace.has-detail {
    grid-template-columns: minmax(0, 1fr) minmax(340px, 440px);

    > :first-child {
      display: none;
    }
  }
}
@media (max-width: 900px) {
  .workspace,
  .workspace.has-detail {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
